import { ref } from 'vue'

// Unité d'affichage de la vitesse du vent, partagée par TOUTE l'interface
// (OSD drone, OSD dock, live vidéo, page météo). Un seul réglage à basculer :
// l'état est un `ref` de module, donc réactif et commun à tous les composants,
// et persisté dans le localStorage pour survivre aux rechargements.
export type WindSpeedUnit = 'ms' | 'kmh'

const WIND_UNIT_PREF_KEY = 'wind-speed-unit'

function loadUnit (): WindSpeedUnit {
  try {
    return localStorage.getItem(WIND_UNIT_PREF_KEY) === 'kmh' ? 'kmh' : 'ms'
  } catch (_) {
    return 'ms' // mode privé / storage indisponible
  }
}

// Source de vérité réactive. Importer et lire `windSpeedUnit.value` (directement
// ou via les helpers ci-dessous) suffit pour que le composant se re-rende au
// changement d'unité.
export const windSpeedUnit = ref<WindSpeedUnit>(loadUnit())

export function setWindSpeedUnit (u: WindSpeedUnit): void {
  windSpeedUnit.value = u
  try {
    localStorage.setItem(WIND_UNIT_PREF_KEY, u)
  } catch (_) { /* mode privé */ }
}

// Bascule m/s ⇆ km/h. C'est le « réglage à toucher ».
export function toggleWindSpeedUnit (): void {
  setWindSpeedUnit(windSpeedUnit.value === 'kmh' ? 'ms' : 'kmh')
}

// Libellé court de l'unité active ('m/s' ou 'km/h').
export function windUnitLabel (): string {
  return windSpeedUnit.value === 'kmh' ? 'km/h' : 'm/s'
}

// Convertit une vitesse exprimée en m/s vers l'unité active (nombre brut).
export function toDisplayWindSpeed (ms: number): number {
  return windSpeedUnit.value === 'kmh' ? ms * 3.6 : ms
}

// Valeur formatée SANS unité (ex. "12.3") dans l'unité active.
// Renvoie '—' si la valeur est absente / non numérique.
export function formatWindValue (ms: number | null | undefined, digits = 1): string {
  if (ms == null || Number.isNaN(Number(ms))) return '—'
  return toDisplayWindSpeed(Number(ms)).toFixed(digits)
}

// Valeur formatée AVEC unité (ex. "12.3 km/h") dans l'unité active.
export function formatWindSpeed (ms: number | null | undefined, digits = 1): string {
  if (ms == null || Number.isNaN(Number(ms))) return '—'
  return formatWindValue(ms, digits) + ' ' + windUnitLabel()
}
